import { useSeoMeta } from '@unhead/react';
import { BarChart3, Clock, Flame, BookOpen, Award, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CEFR_LEVELS, CEFR_DESCRIPTIONS, SUPPORTED_LANGUAGES, type CEFRLevel } from '@/types/language';

const SAMPLE_STATS = {
  wordsLearned: 45,
  wordsMastered: 12,
  totalReviews: 156,
  streak: 3,
  longestStreak: 7,
  studyMinutes: 320,
};

const CEFR_PROGRESS: Record<CEFRLevel, number> = {
  'A1': 35,
  'A2': 8,
  'B1': 0,
  'B2': 0,
  'C1': 0,
  'C2': 0,
};

const ProgressPage = () => {
  useSeoMeta({
    title: 'Progress - LinguaNostr',
    description: 'Track your language learning progress with detailed statistics.',
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Progress</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">Track your learning journey</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        {[
          { icon: BookOpen, label: 'Words Learning', value: SAMPLE_STATS.wordsLearned, color: 'text-blue-600' },
          { icon: Award, label: 'Mastered', value: SAMPLE_STATS.wordsMastered, color: 'text-green-600' },
          { icon: TrendingUp, label: 'Reviews Done', value: SAMPLE_STATS.totalReviews, color: 'text-purple-600' },
          { icon: Flame, label: 'Day Streak', value: `${SAMPLE_STATS.streak}`, color: 'text-orange-600' },
          { icon: Clock, label: 'Study Time', value: `${SAMPLE_STATS.studyMinutes}m`, color: 'text-cyan-600' },
          { icon: Award, label: 'Best Streak', value: `${SAMPLE_STATS.longestStreak}d`, color: 'text-amber-600' },
        ].map((stat) => (
          <Card key={stat.label} className="border-slate-200 dark:border-slate-800">
            <CardHeader className="flex flex-row items-center gap-3 pb-2">
              <stat.icon className={`h-5 w-5 ${stat.color}`} />
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {stat.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* CEFR Progress */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg text-slate-900 dark:text-white">CEFR Level Progress — Spanish</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {CEFR_LEVELS.map((level) => {
            const progress = CEFR_PROGRESS[level];
            return (
              <div key={level}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-900 dark:text-white">{level}</span>
                    <span className="text-slate-500 dark:text-slate-400">{CEFR_DESCRIPTIONS[level]}</span>
                  </div>
                  <span className="font-medium text-slate-700 dark:text-slate-300">{progress}%</span>
                </div>
                <Progress 
                  value={progress} 
                  className={cn(
                    'h-2',
                    progress >= 80 ? 'bg-green-100 [&>div]:bg-green-500' :
                    progress >= 40 ? 'bg-blue-100 [&>div]:bg-blue-500' :
                    'bg-slate-100 [&>div]:bg-slate-400'
                  )} 
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Active Languages */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg text-slate-900 dark:text-white">Active Languages</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {SUPPORTED_LANGUAGES.filter((l) => l.code !== 'en').slice(0, 3).map((lang) => (
            <div key={lang.code} className="flex items-center gap-4 rounded-lg bg-slate-50 p-4 dark:bg-slate-900">
              <span className="text-3xl">{lang.flag}</span>
              <div className="flex-1">
                <p className="font-semibold text-slate-900 dark:text-white">{lang.name}</p>
                <p className="text-sm text-slate-500">{lang.nativeName}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-slate-900 dark:text-white">A1</p>
                <p className="text-sm text-green-600">In progress</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

export default ProgressPage;
